import Immutable from 'immutable'

/**
 * Matches the full color declaration for one palette color, with or without
 * a fallback, including a trailing semicolon. The name boundary keeps color4
 * from matching color40. Examples: `color:var(--colors-color4, inherit)`,
 * `color:var(--colors-color4)`.
 */
const colorDeclarationPattern = (colorName: string) =>
  new RegExp(String.raw`(?<![\w-])color\s*:\s*var\(--colors-${colorName}\s*(?:,[^)]*)?\)\s*;?`, 'g')

/**
 * The ref a field is set to when its ref to a removed color has no
 * replacement: null for every field, except `buttonColorRef`, which is set to
 * the `button` token that banner elements are created with.
 */
const fallbackRef = (key: string): string | null => (key === 'buttonColorRef' ? 'button' : null)

/**
 * Immutable counterpart of the backend content walk (fusion-backend-ts
 * src/utils/contentColorRefs.ts): after the server-side remap, the same
 * rewrite is applied to the cached page content in the store. Refs are set
 * to the replacement color's ref, or to the field's fallback ref (see
 * fallbackRef) when no replacement is given. Rich-text
 * `color:var(--colors-colorN, ...)` declarations inside HTML strings are
 * renamed to the replacement's var(), or stripped entirely when no
 * replacement is given.
 */
export function remapColorRefs(node: unknown, colorName: string, replacementColorName?: string): unknown {
  const targetRef = `colors.${colorName}`
  const replacementRef = replacementColorName ? `colors.${replacementColorName}` : null
  const remappedRef = (key: string) => replacementRef ?? fallbackRef(key)

  const declarationPattern = colorDeclarationPattern(colorName)
  const rewriteColorDeclarations = (html: string) =>
    html.replace(declarationPattern, (declaration) =>
      replacementColorName ? declaration.replace(`--colors-${colorName}`, `--colors-${replacementColorName}`) : '',
    )

  const walk = (value: unknown): unknown => {
    if (typeof value === 'string') {
      return value.includes('var(--colors-') ? rewriteColorDeclarations(value) : value
    }
    if (Immutable.List.isList(value)) return (value as ImmutableList).map(walk)
    if (Immutable.Map.isMap(value)) {
      return (value as ImmutableMap).map((child: unknown, key: unknown) =>
        typeof key === 'string' && key.endsWith('Ref') && child === targetRef ? remappedRef(key) : walk(child),
      )
    }
    // Plain js nodes occur inside the Immutable content: Block.tsx saves
    // backgroundColorSettings as a plain object on the block.
    if (Array.isArray(value)) return value.map(walk)
    if (value !== null && typeof value === 'object') {
      return Object.fromEntries(
        Object.entries(value).map(([key, child]) => [
          key,
          key.endsWith('Ref') && child === targetRef ? remappedRef(key) : walk(child),
        ]),
      )
    }
    return value
  }

  return walk(node)
}
